// Which drums a phase can be planned FROM, and when one of them is spent.
//
// The "Incoming executions" panel lists every drum the previous phase will hand
// over — the real ones, and the stock reels and phantoms that stand in for
// drums somebody else wound. The planner ticks the ones a run draws on.
//
// ── the fenomen ─────────────────────────────────────────────────────────────
//
// Reported 2026-10-27: "cand atingem cantitatea lansata printr-un add
// execution … pare sa ne puna label cu fully programmed, dar ramane selectat.
// daca il deselectez nu mai pot sa il selectez odata."
//
// Adding an execution that takes the last of a ticked drum left it ticked. The
// card went green and said "Fully programmed", but the tick was still there, so:
//
//   · the next run still named it as an input, and the gate on "+ Add
//     Execution" read "Selected input is fully allocated" and refused —
//     the planner was blocked by a drum they thought they had finished with;
//   · deselecting it by hand made it unselectable (`locked = finished &&
//     !isSelected`), which looks like the click broke something.
//
// A spent drum should simply leave the selection. The tick means "this run
// draws on this drum", and a drum with nothing left cannot be drawn on.
//
// ── and the trap in fixing it ───────────────────────────────────────────────
//
// Emptying the selection is not free: in CONTINUE mode `isBlocked` is relaxed
// (`!isContinueMode || isEditDownstreamPhase`), so `upstreamFullyAllocated` was
// the ONLY thing holding "+ Add Execution" shut. Drop the drum and the button
// would open again with nothing behind it. So the gate has to stop asking "is
// what I selected spent?" and ask "is there anything left to select?" —
// `noDrumsLeft` — which is also the better question: with three drums and only
// the first one spent, the old gate refused a run that could simply have used
// the second.

import { drumCapacity, drumRemaining } from './drumRemaining.js';

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

/**
 * Every drum the previous phase hands to this one, in the order the panel draws
 * them.
 *
 * One definition, because two places need the same list: the panel that draws
 * the ticks and the gate that decides whether a run can be added at all. They
 * had one each, and the gate's was the shorter one — it could not see a phantom
 * injected from `_parentTokenIds`, so a phase whose only remaining input was a
 * stock reel looked exhausted.
 *
 * @param product the wizard product (carries `_continueMode`, `_existingTokens`,
 *                `_parentTokenIds` when continuing a launched job)
 * @param phase   the phase being planned — its `dependsOn` names the previous one
 */
export function incomingExecutions(product, phase) {
  const previousPhase =
    phase?.dependsOn && product?.phases?.find((p) => p.name === phase.dependsOn);
  if (!previousPhase) return [];

  const out = [];

  if (previousPhase.conductors) {
    for (const cond of previousPhase.conductors) {
      for (const exec of cond.execution || []) {
        out.push({ color: cond?.isolation?.color, ...exec });
      }
    }
  } else if (previousPhase.execution) {
    const all = (Array.isArray(previousPhase.execution) ? previousPhase.execution : []).map(
      (exec, idx) => ({ ...exec, id: exec.id ?? idx + 1 })
    );

    // A drum routed to coiling is not on offer to rewinding, and the other way
    // round — but if nothing states a route, everything is on offer.
    if (phase?.name === 'coiling' || phase?.name === 'rewinding') {
      const routed = all.filter((e) => e.nextPhase === phase.name);
      out.push(...(routed.length ? routed : all));
    } else {
      out.push(...all);
    }
  }

  // Phantoms held as parents of this job that no execution describes — a reel
  // taken from stock after the launch, which exists only as a token.
  if (product?._continueMode && product?._existingTokens && product?._parentTokenIds) {
    const seen = new Set(out.map((e) => e.tokenId).filter(Boolean));
    for (const tid of product._parentTokenIds) {
      if (!String(tid).startsWith('PH-') || seen.has(tid)) continue;
      const tok = product._existingTokens[tid];
      if (!tok) continue;
      out.push({
        id: tid,
        tokenId: tid,
        plannedLength: tok.availableLength || tok.plannedLength || 0,
        color: tok.color || null,
        isPhantom: true,
        phantomData: tok.phantomData || null,
      });
    }
  }

  return out;
}

/** The drum behind an execution, when the job is already launched. */
export function tokenOf(product, exec) {
  return product?._continueMode && exec?.tokenId
    ? product?._existingTokens?.[exec.tokenId] || null
    : null;
}

/**
 * Has this drum got anything left to plan against?
 *
 * The SAME rule as the card, `toggleSelect` and the Add gate —
 * `drumRemaining` from helpers/drumRemaining.js. A drum with no capacity at all
 * (an empty one, or a husk whose metres were forked away) counts as spent: it
 * has nothing to give either.
 */
export function drumIsSpent({ exec, product, used = 0 }) {
  return drumRemaining({ exec, token: tokenOf(product, exec), used: num(used) }) <= 0;
}

/**
 * How much of a drum is already spoken for.
 *
 * `usedOf` wins over `consumedLengths` so each caller can hand over the figure
 * its own context knows. They must agree, and they do: the panel passes
 * `max(buildConsumedMapForPhase, usedOnDrum(phasePlanned: 0))` and the gate
 * passes `usedOnDrum(phasePlanned: buildConsumedMapForPhase)` — and
 * `usedOnDrum` is a max over those same four figures, so the two reduce to the
 * same number. Worth stating, because a drum that is spent to one and live to
 * the other is exactly how one could sit in the selection while drawn as
 * unselectable.
 */
const usedFor = (exec, consumedLengths, usedOf) =>
  typeof usedOf === 'function' ? num(usedOf(exec)) : num(consumedLengths?.[exec?.id]);

/**
 * The ids of every incoming drum with nothing left.
 *
 * @param consumedLengths { [execId]: metres } — what the phase's own executions
 *                        already take off each drum, as the panel computes it
 * @param usedOf          (exec) => metres, if the caller has a better answer
 */
export function spentDrumIds({
  product,
  phase,
  consumedLengths = {},
  usedOf = null,
  execs = null
}) {
  const list = Array.isArray(execs) ? execs : incomingExecutions(product, phase);
  return list
    .filter((exec) => drumIsSpent({ exec, product, used: usedFor(exec, consumedLengths, usedOf) }))
    .map((exec) => exec.id);
}

/**
 * Is there NO drum left to plan from?
 *
 * What the "+ Add Execution" gate should ask. `false` when there are no
 * incoming drums at all — a phase with no upstream is not blocked by this, it
 * is governed by its own rules.
 */
export function noDrumsLeft({
  product,
  phase,
  consumedLengths = {},
  usedOf = null,
  execs = null
}) {
  const list = Array.isArray(execs) ? execs : incomingExecutions(product, phase);
  if (!list.length) return false;
  return list.every((exec) =>
    drumIsSpent({ exec, product, used: usedFor(exec, consumedLengths, usedOf) })
  );
}

/**
 * What the selection should become once the spent drums leave it.
 *
 * `changed` is a separate answer from `selection`, because "nothing to do" and
 * "clear it" are both falsy in the single-select shape and the caller writes
 * the result into state — telling them apart by the value alone would either
 * write on every render (a loop) or never write at all.
 *
 * Multi-select keeps its order and its other drums. Single-select becomes
 * `null`, which is what "nothing selected" means in that shape.
 *
 * @returns { changed, selection }
 */
export function selectionAfterSpent({ selection, spentIds = [], isMultiSelect = false }) {
  const spent = new Set((Array.isArray(spentIds) ? spentIds : []).map(String));

  if (isMultiSelect) {
    const list = Array.isArray(selection) ? selection : [];
    const kept = spent.size ? list.filter((e) => !spent.has(String(e?.id))) : list;
    return kept.length === list.length
      ? { changed: false, selection: list }
      : { changed: true, selection: kept };
  }

  const one = selection || null;
  const drop = !!one && spent.has(String(one.id));
  return { changed: drop, selection: drop ? null : one };
}

const incomingDrums = {
  incomingExecutions,
  tokenOf,
  drumIsSpent,
  spentDrumIds,
  noDrumsLeft,
  selectionAfterSpent,
  drumCapacity,
};
export default incomingDrums;
